import { arrayMove } from "@dnd-kit/sortable";

import ENV from "config/Env";
import { typedQueryClient, useTypedMutation } from "hooks";
import { AppointmentDetailsKeys } from "modules/AppointmentDetails/queryKeys";
import { ApiInstance } from "utils/Api";

import type { InterventionPendingChecklist } from "models";

type ReorderPendingChecklistPayload = {
  items: InterventionPendingChecklist[];
  oldIndex: number;
  newIndex: number;
};

const reorderPayload = ({ items, oldIndex, newIndex }: ReorderPendingChecklistPayload) => {
  const [start, end] = oldIndex < newIndex ? [oldIndex, newIndex] : [newIndex, oldIndex];

  return arrayMove(items, oldIndex, newIndex)
    .map((i, idx) => ({ ...i, ordering: idx + 1 }))
    .filter((_, idx) => idx >= start && idx <= end);
};

export const useReorderPendingChecklistMutation = () =>
  useTypedMutation({
    mutationFn: (payload: ReorderPendingChecklistPayload) =>
      ApiInstance.post("/interventions/pending_checklists/reorder", { pending_checklists: reorderPayload(payload) }, ENV.appointmentBaseURL),
    onSuccess: ({ items, oldIndex, newIndex }) => {
      const appointment = typedQueryClient.getQueryData(AppointmentDetailsKeys.view);
      if (!appointment?.interventions || !items.length) return;

      const interventionIdx = appointment.interventions.findIndex(i => i.id === items[0].intervention_id);
      if (interventionIdx < 0 || !appointment.interventions[interventionIdx].pending_checklists?.length) return;

      typedQueryClient.setQueryData(AppointmentDetailsKeys.view, {
        ...appointment,
        interventions: appointment.interventions.with(interventionIdx, {
          ...appointment.interventions[interventionIdx],
          pending_checklists: arrayMove(appointment.interventions[interventionIdx].pending_checklists, oldIndex, newIndex)
        })
      });
    }
  });
